import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useMarketplace } from '../../context/MarketplaceContext';
import { useTranslation } from 'react-i18next';
import { 
  createVerification, 
  uploadSelfieImage, 
  uploadSupportingDocument,
  getVerificationByStoreId
} from '../../utils/verificationDatabase';
import { calculateVerificationScore, getVerificationCost, loadVerificationCostConfig } from '../../data/verificationModels';
import { compressImage } from '../../utils/imageUtils';
import { useTCoins } from '../../context/TCoinsContext';
import AdminVideoCallBookingModal from './AdminVideoCallBookingModal';

const StoreVerificationForm = ({ store, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { getStoreSubscription } = useMarketplace();
  const { getTotalTCoins, getTotalReal, getTotalVirtual, deductTCoins } = useTCoins();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Basic Info, 2: Selfie, 3: Documents, 4: Review
  const [existingVerification, setExistingVerification] = useState(null);
  
  // Form Data
  const [formData, setFormData] = useState({
    personalInfo: {
      fullName: '',
      idCardLast4: '',
      birthDate: '', // DD/MM format
    },
    selfieImage: null,
    selfiePreview: null,
    supportingDocuments: {
      commercialRegistration: { file: null, preview: null },
      cannabisLicense: { file: null, preview: null },
      addressProof: { file: null, preview: null },
      taxProof: { file: null, preview: null },
    },
    consent: false,
  });
  
  const [verificationId, setVerificationId] = useState(null);
  const [verificationType, setVerificationType] = useState('initial'); // initial or renewal
  const [cost, setCost] = useState(500);
  const [packageInfo, setPackageInfo] = useState(null);
  const [showVideoCallModal, setShowVideoCallModal] = useState(false);
  
  useEffect(() => {
    if (store?.id) {
      loadExistingVerification();
      loadPackageInfo();
      loadVerificationCostConfig(); // Load config once
    }
  }, [store]);
  
  useEffect(() => {
    if (existingVerification) {
      setVerificationType('renewal');
      setCost(getVerificationCost('renewal'));
    } else {
      setVerificationType('initial');
      setCost(getVerificationCost('initial'));
    }
  }, [existingVerification]);
  
  const loadExistingVerification = async () => {
    try {
      const result = await getVerificationByStoreId(store.id);
      if (result.success && result.verification) {
        setExistingVerification(result.verification);
        // Load existing data if renewal
        if (result.verification.status === 'expired' || result.verification.status === 'rejected') {
          setFormData(prev => ({
            ...prev,
            personalInfo: {
              fullName: result.verification.personalInfo?.fullName || '',
              idCardLast4: result.verification.personalInfo?.idCardLast4 || '',
              birthDate: result.verification.personalInfo?.birthDate || '',
            },
          }));
        }
      }
    } catch (error) {
      console.error('Error loading existing verification:', error);
    }
  };
  
  const loadPackageInfo = async () => {
    try {
      if (store?.id) {
        const subscription = await getStoreSubscription(store.id);
        setPackageInfo(subscription);
        // Check if package covers verification cost
        if (subscription?.packageType === 'premium' || subscription?.packageType === 'enterprise') {
          // Premium/Enterprise get free initial verification
          if (verificationType === 'initial') {
            setCost(0);
          }
        }
      }
    } catch (error) {
      console.error('Error loading package info:', error);
    }
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      personalInfo: {
        ...prev.personalInfo,
        [field]: value,
      },
    }));
  };
  
  const handleSelfieUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      alert(t('store.fileMustBeImage'));
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      alert(t('store.fileTooLarge'));
      return;
    }
    
    try {
      // Compress image
      const compressedFile = await compressImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          selfieImage: compressedFile,
          selfiePreview: reader.result,
        }));
      };
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      console.error('Error processing selfie image:', error);
      alert(t('store.uploadError') + error.message);
    }
  };
  
  const handleDocumentUpload = async (documentType, e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      alert('กรุณาอัพโหลดไฟล์รูปภาพหรือ PDF');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      alert('ไฟล์ใหญ่เกินไป (สูงสุด 10MB)');
      return;
    }
    
    try {
      let processedFile = file;
      let preview = null;
      
      // If image, compress it
      if (file.type.startsWith('image/')) {
        processedFile = await compressImage(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData(prev => ({
            ...prev,
            supportingDocuments: {
              ...prev.supportingDocuments,
              [documentType]: {
                file: processedFile,
                preview: reader.result,
              },
            },
          }));
        };
        reader.readAsDataURL(processedFile);
      } else {
        // PDF - just store file
        setFormData(prev => ({
          ...prev,
          supportingDocuments: {
            ...prev.supportingDocuments,
            [documentType]: {
              file: processedFile,
              preview: null,
            },
          },
        }));
      }
    } catch (error) {
      console.error('Error processing document:', error);
      alert('เกิดข้อผิดพลาดในการประมวลผลไฟล์: ' + error.message);
    }
  };
  
  const removeDocument = (documentType) => {
    setFormData(prev => ({
      ...prev,
      supportingDocuments: {
        ...prev.supportingDocuments,
        [documentType]: { file: null, preview: null },
      },
    }));
  };
  
  const calculateScore = () => {
    // Base score from selfie (if uploaded)
    let score = formData.selfieImage ? 100 : 0;
    
    // Add document scores (will be approved by admin)
    // For now, just show potential scores
    const documentScores = {
      commercialRegistration: 20,
      cannabisLicense: 30,
      addressProof: 10,
      taxProof: 15,
    };
    
    Object.keys(formData.supportingDocuments).forEach(docType => {
      if (formData.supportingDocuments[docType].file) {
        score += documentScores[docType] || 0;
      }
    });
    
    return Math.min(score, 175);
  };
  
  const validateStep = (currentStep) => {
    switch (currentStep) {
      case 1: // Basic Info
        if (!formData.personalInfo.fullName.trim()) {
          alert('กรุณากรอกชื่อ-นามสกุล');
          return false;
        }
        if (!formData.personalInfo.idCardLast4 || formData.personalInfo.idCardLast4.length !== 4) {
          alert('กรุณากรอกเลขบัตรประชาชน 4 หลักสุดท้าย');
          return false;
        }
        if (!formData.personalInfo.birthDate || !/^\d{2}\/\d{2}$/.test(formData.personalInfo.birthDate)) {
          alert('กรุณากรอกวันเกิดในรูปแบบ DD/MM (เช่น 15/03)');
          return false;
        }
        return true;
      
      case 2: // Selfie
        if (!formData.selfieImage) {
          alert('กรุณาถ่ายรูป Selfie');
          return false;
        }
        return true;
      
      case 3: // Documents (Optional)
        return true; // Documents are optional
      
      case 4: // Review
        if (!formData.consent) {
          alert('กรุณายินยอมให้เก็บข้อมูลตาม Privacy Policy');
          return false;
        }
        return true;
      
      default:
        return true;
    }
  };
  
  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };
  
  const handleBack = () => {
    setStep(step - 1);
  };
  
  const handleSubmit = async () => {
    if (!validateStep(4)) return;
    
    // Check T Coins balance
    const totalTCoins = getTotalTCoins();
    if (cost > 0 && totalTCoins < cost) {
      alert(`T Coins ไม่พอ (ต้องการ ${cost} T Coins, มี ${totalTCoins} T Coins)`);
      return;
    }
    
    try {
      setLoading(true);
      
      // Create verification
      const verificationData = {
        personalInfo: formData.personalInfo,
        verificationType,
        cost: {
          amount: cost,
          paymentMethod: cost === 0 ? 'package' : 'tcoins',
        },
      };
      
      let result;
      if (existingVerification) {
        // Update existing verification
        result = await createVerification(store.id, currentUser.uid, {
          ...verificationData,
          id: existingVerification.id,
        });
      } else {
        // Create new verification
        result = await createVerification(store.id, currentUser.uid, verificationData);
      }
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      const newVerificationId = result.verificationId;
      setVerificationId(newVerificationId);
      
      // Upload selfie
      if (formData.selfieImage) {
        const selfieResult = await uploadSelfieImage(formData.selfieImage, newVerificationId);
        if (!selfieResult.success) {
          throw new Error(selfieResult.error);
        }
      }
      
      // Upload documents
      for (const [docType, docData] of Object.entries(formData.supportingDocuments)) {
        if (docData.file) {
          const docResult = await uploadSupportingDocument(docData.file, newVerificationId, docType);
          if (!docResult.success) {
            console.error(`Error uploading ${docType}:`, docResult.error);
            // Continue with other documents
          }
        }
      }
      
      // Deduct T Coins if needed (ใช้ real coins ก่อน ถ้าไม่พอใช้ virtual)
      if (cost > 0) {
        const totalReal = getTotalReal();
        const totalVirtual = getTotalVirtual();
        
        // ใช้ real coins ก่อน ถ้าไม่พอใช้ virtual
        if (totalReal >= cost) {
          await deductTCoins(currentUser.uid, 'real', cost, `store_verification_${verificationType}`);
        } else if (totalReal + totalVirtual >= cost) {
          // ใช้ real ทั้งหมด แล้วใช้ virtual ที่เหลือ
          if (totalReal > 0) {
            await deductTCoins(currentUser.uid, 'real', totalReal, `store_verification_${verificationType}_partial`);
          }
          const remainingCost = cost - totalReal;
          await deductTCoins(currentUser.uid, 'virtual', remainingCost, `store_verification_${verificationType}_virtual`);
        } else {
          throw new Error(`T Coins ไม่พอ (ต้องการ ${cost} T Coins)`);
        }
      }
      
      alert('ส่งคำขอตรวจสอบร้านค้าเรียบร้อยแล้ว! แอดมินจะตรวจสอบและแจ้งผลภายใน 2-3 วันทำการ');
      
      if (onSuccess) {
        onSuccess();
      }
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error submitting verification:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const documentLabels = {
    commercialRegistration: { name: 'ทะเบียนพาณิชย์', score: 20 },
    cannabisLicense: { name: 'ใบอนุญาตขายกัญชา', score: 30 },
    addressProof: { name: 'เอกสารยืนยันที่อยู่', score: 10 },
    taxProof: { name: 'หลักฐานการชำระภาษี', score: 15 },
  };
  
  const potentialScore = calculateScore();
  
  if (!store) {
    return <div className="p-4">กรุณาเปิดร้านก่อน</div>;
  }
  
  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">
        📋 {verificationType === 'renewal' ? 'ต่ออายุการตรวจสอบร้านค้า' : 'ขอตรวจสอบร้านค้า'}
      </h2>
      
      {/* Progress Steps */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= s ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                {step > s ? '✓' : s}
              </div>
              {s < 4 && (
                <div className={`flex-1 h-1 mx-2 ${
                  step > s ? 'bg-green-500' : 'bg-gray-300'
                }`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-600">
          <span>ข้อมูลพื้นฐาน</span>
          <span>Selfie</span>
          <span>เอกสาร</span>
          <span>ตรวจสอบ</span>
        </div>
      </div>
      
      {/* Step 1: Basic Info */}
      {step === 1 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-gray-700">1️⃣ ข้อมูลพื้นฐาน</h3>
          
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              ชื่อ-นามสกุล <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.personalInfo.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="สมชาย ใจดี"
            />
          </div>
          
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              เลขบัตรประชาชน 4 หลักสุดท้าย <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              maxLength="4"
              value={formData.personalInfo.idCardLast4}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                if (value.length <= 4) {
                  handleInputChange('idCardLast4', value);
                }
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="1234"
            />
            <p className="text-xs text-gray-500 mt-1">เก็บเฉพาะ 4 หลักสุดท้ายเพื่อความปลอดภัย</p>
          </div>
          
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              วันเกิด <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              maxLength="5"
              value={formData.personalInfo.birthDate}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                let formatted = value;
                if (value.length >= 2) {
                  formatted = value.slice(0, 2) + '/' + value.slice(2, 4);
                }
                handleInputChange('birthDate', formatted);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="15/03"
            />
            <p className="text-xs text-gray-500 mt-1">รูปแบบ: DD/MM (ไม่เก็บปี)</p>
          </div>
        </div>
      )}
      
      {/* Step 2: Selfie Verification */}
      {step === 2 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-gray-700">2️⃣ ยืนยันตัวตนด้วย Selfie</h3>
          
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            {formData.selfiePreview ? (
              <div>
                <img 
                  src={formData.selfiePreview} 
                  alt="Selfie Preview" 
                  className="max-w-full max-h-64 mx-auto rounded-lg mb-4"
                />
                <button
                  onClick={() => setFormData(prev => ({ ...prev, selfieImage: null, selfiePreview: null }))}
                  className="text-red-500 hover:text-red-700"
                >
                  ลบรูป
                </button>
              </div>
            ) : (
              <div>
                <label className="cursor-pointer">
                  <div className="text-4xl mb-2">📷</div>
                  <div className="text-gray-600 mb-2">คลิกเพื่อถ่ายรูป Selfie</div>
                  <input
                    type="file"
                    accept="image/*"
                    capture="user"
                    onChange={handleSelfieUpload}
                    className="hidden"
                  />
                </label>
              </div>
            )}
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-gray-700 font-semibold mb-2">✅ ข้อกำหนด:</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>✅ ใบหน้าเห็นชัด</li>
              <li>✅ ไม่มีแว่นตาดำ</li>
              <li>✅ แสงสว่างเพียงพอ</li>
              <li>✅ ไม่มีคนอื่นในภาพ</li>
            </ul>
          </div>
        </div>
      )}
      
      {/* Step 3: Supporting Documents */}
      {step === 3 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-gray-700">3️⃣ เอกสารประกอบ (ไม่บังคับ)</h3>
          <p className="text-sm text-gray-600 mb-4">
            เอกสารเหล่านี้จะให้คะแนนเพิ่มเติมเมื่อผ่านการตรวจสอบ
          </p>
          
          {Object.entries(documentLabels).map(([docType, label]) => (
            <div key={docType} className="border border-gray-300 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!formData.supportingDocuments[docType].file}
                    onChange={() => {
                      if (formData.supportingDocuments[docType].file) {
                        removeDocument(docType);
                      } else {
                        document.getElementById(`doc-${docType}`).click();
                      }
                    }}
                    className="mr-2"
                  />
                  <span className="font-semibold">
                    {label.name} <span className="text-green-600">(+{label.score} คะแนน)</span>
                  </span>
                </label>
                {formData.supportingDocuments[docType].file && (
                  <button
                    onClick={() => removeDocument(docType)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    ลบ
                  </button>
                )}
              </div>
              
              <input
                id={`doc-${docType}`}
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => handleDocumentUpload(docType, e)}
                className="hidden"
              />
              
              {formData.supportingDocuments[docType].preview && (
                <div className="mt-2">
                  <img 
                    src={formData.supportingDocuments[docType].preview} 
                    alt={label.name}
                    className="max-w-full max-h-32 rounded"
                  />
                </div>
              )}
              
              {formData.supportingDocuments[docType].file && 
               !formData.supportingDocuments[docType].preview && (
                <div className="mt-2 text-sm text-gray-600">
                  📄 {formData.supportingDocuments[docType].file.name}
                </div>
              )}
            </div>
          ))}
          
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-sm font-semibold text-gray-700">
              📊 คะแนนรวมที่อาจได้: {potentialScore}/175 คะแนน
            </p>
            <p className="text-xs text-gray-600 mt-1">
              (100 คะแนนจาก Selfie + {potentialScore - 100} คะแนนจากเอกสาร)
            </p>
          </div>
        </div>
      )}
      
      {/* Step 4: Review & Submit */}
      {step === 4 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-gray-700">4️⃣ ตรวจสอบและส่ง</h3>
          
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <p><strong>ชื่อ-นามสกุล:</strong> {formData.personalInfo.fullName}</p>
            <p><strong>เลขบัตร 4 หลัก:</strong> {formData.personalInfo.idCardLast4}</p>
            <p><strong>วันเกิด:</strong> {formData.personalInfo.birthDate}</p>
            <p><strong>Selfie:</strong> {formData.selfieImage ? '✓ อัพโหลดแล้ว' : '✗ ยังไม่อัพโหลด'}</p>
            <p><strong>เอกสารประกอบ:</strong> {
              Object.values(formData.supportingDocuments).filter(doc => doc.file).length
            } ไฟล์</p>
            <p><strong>คะแนนที่อาจได้:</strong> {potentialScore}/175</p>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg mb-4">
            <p className="font-semibold text-gray-700 mb-2">💰 ค่าใช้จ่าย:</p>
            <p className="text-2xl font-bold text-green-600">
              {cost === 0 ? 'ฟรี (ใช้ Package)' : `${cost} เหรียญ`}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              💳 T Coins ที่เหลือ: {getTotalTCoins().toLocaleString()} T Coins
            </p>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="text-sm text-gray-700 mb-2">
              💡 ต้องการ Video Call กับแอดมินเพื่อตรวจสอบร้านค้า?
            </p>
            <button
              onClick={() => setShowVideoCallModal(true)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
            >
              📹 จอง Video Call กับแอดมิน
            </button>
          </div>
          
          <div className="flex items-start">
            <input
              type="checkbox"
              id="consent"
              checked={formData.consent}
              onChange={(e) => setFormData(prev => ({ ...prev, consent: e.target.checked }))}
              className="mt-1 mr-2"
            />
            <label htmlFor="consent" className="text-sm text-gray-700">
              ฉันยินยอมให้เก็บข้อมูลตาม <a href="/privacy-policy" className="text-blue-500 hover:underline">Privacy Policy</a>
            </label>
          </div>
        </div>
      )}
      
      {/* Navigation Buttons */}
      <div className="flex justify-between mt-6">
        <button
          onClick={step > 1 ? handleBack : onClose}
          className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
          disabled={loading}
        >
          {step > 1 ? 'ย้อนกลับ' : 'ยกเลิก'}
        </button>
        
        {step < 4 ? (
          <button
            onClick={handleNext}
            className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            ถัดไป →
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={loading || !formData.consent}
            className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading ? 'กำลังส่ง...' : 'ส่งให้แอดมินตรวจสอบ'}
          </button>
        )}
      </div>
      
      {/* Video Call Booking Modal */}
      <AdminVideoCallBookingModal
        isOpen={showVideoCallModal}
        onClose={() => setShowVideoCallModal(false)}
        onSuccess={() => {
          setShowVideoCallModal(false);
          if (onSuccess) onSuccess();
        }}
        store={store}
        purpose="verification"
      />
    </div>
  );
};

export default StoreVerificationForm;

